import { useTranslation } from "react-i18next";
import type { EditorialBlock } from "@majourneys/shared";
import type { AiMatch } from "../api/ai.api";
import type { ChatLang } from "../hooks/use-ai-chat";
import { LayoutLabel } from "./layout-parts";
import { MatchGrid } from "./match-card";
import { RADIUS, SOFT } from "./soft-tokens";

/**
 * Voice header of an editorial answer: kicker overline, headline, lede, gold
 * fact pills. Ported from the phone's editorial-header.tsx.
 *
 * Pure presentation — every string arrives from the API's `editorial` block,
 * already clamped server-side. A renderer must never source a fact from here;
 * prices, titles and images come from the grounded rows.
 */
export function EditorialHeader({ block }: { block: EditorialBlock }) {
  return (
    <div className="mb-3.5">
      <div
        className="text-[11px] font-bold uppercase tracking-[1.2px]"
        style={{ color: SOFT.goldInk }}
      >
        {block.kicker}
      </div>
      <h3
        className="mt-1.5 text-start text-[20px] font-semibold leading-[26px]"
        style={{ color: SOFT.ink }}
      >
        {block.headline}
      </h3>
      <p className="mt-2 text-start text-[14px] leading-[22px]" style={{ color: SOFT.ink }}>
        {block.lede}
      </p>
      {block.facts && block.facts.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {block.facts.map((f) => (
            <span
              key={f}
              className="px-2.5 py-1 text-[11.5px] font-semibold"
              style={{
                borderRadius: RADIUS.pill,
                // Chat kit: a fact is a quiet navy-tinted chip, the same as
                // the welcome's trip context. Gold stays scarce.
                background: SOFT.tint,
                color: SOFT.ink,
              }}
            >
              {f}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * What the trip costs AROUND the things we sell.
 *
 * Phone ruling (estimate-block.tsx): a price for something WE SELL is never
 * estimated, and these numbers are not that — a bed, meals out, getting
 * around, spending money, none of it ours. So it says "estimated" and it says
 * who for. A range with no label reads as a quote, and a quote we did not
 * check is the failure this feature spent a day removing.
 *
 * Deliberately plain: no gold, no pills, no card. The Reserve cards above are
 * things you can book; this is arithmetic about everything else and must not
 * look like an offer.
 */
export function EstimateBlock({
  estimate,
}: {
  estimate: NonNullable<EditorialBlock["estimate"]>;
}) {
  const { t } = useTranslation();
  const rule = "1px solid rgba(17,14,57,0.10)";
  return (
    <div className="mb-1 mt-[18px]">
      <div
        className="mb-2.5 text-[11px] font-extrabold uppercase tracking-[1.4px]"
        style={{ color: SOFT.body }}
      >
        {t("ai_concierge.estimated_cost", { defaultValue: "Estimated cost" })}
      </div>
      {estimate.lines.map((l) => (
        <div
          key={`${l.label}-${l.range}`}
          className="flex items-center justify-between gap-3 py-2"
          style={{ borderTop: rule }}
        >
          <span className="min-w-0 text-[14px]" style={{ color: SOFT.body }}>
            {l.label}
          </span>
          <span className="text-[14px] font-semibold" style={{ color: SOFT.ink }}>
            {l.range}
          </span>
        </div>
      ))}
      <div
        className="flex items-center justify-between gap-3 py-2"
        style={{ borderTop: rule }}
      >
        <span className="text-[14px] font-extrabold" style={{ color: SOFT.ink }}>
          {t("ai_concierge.estimated_total", { defaultValue: "Estimated total" })}
        </span>
        <span className="text-[14px] font-extrabold" style={{ color: SOFT.ink }}>
          {estimate.total}
        </span>
      </div>
      <p className="mt-2 text-[12px]" style={{ color: SOFT.body }}>
        {estimate.basis}
      </p>
    </div>
  );
}

/**
 * Themed answer shelves: each section is a title, a one-line rationale, and
 * that section's own grounded rows. Ported from the phone's
 * editorial-sections.tsx. Each section's cards sit on the chat kit's sideways
 * snap rail (C5), like the phone's.
 *
 * Sections arrive pre-filtered by the API, so an unknown slug simply does not
 * render.
 */
export function EditorialSections({
  sections,
  matches,
  lang,
  inTrip,
  onAdd,
  why,
  numbers,
}: {
  sections: NonNullable<EditorialBlock["sections"]>;
  matches: AiMatch[];
  lang: ChatLang;
  inTrip: Set<string>;
  onAdd: (id: string) => void;
  /** Why each place, for this question, keyed by slug. */
  why?: Record<string, string>;
  /** Slug to pin number, forwarded to the cards so card 7 is pin 7. */
  numbers?: ReadonlyMap<string, number>;
}) {
  const bySlug = new Map(matches.map((m) => [m.slug, m]));
  return (
    <div>
      {sections.map((section) => {
        const rows = section.slugs
          .map((slug) => bySlug.get(slug))
          .filter((m): m is AiMatch => !!m)
          // The section's own line about the place replaces the catalogue
          // subtitle on the card, the way the phone passes `why` through.
          .map((m) => (why?.[m.slug] ? { ...m, subtitle: why[m.slug]! } : m));
        if (rows.length === 0) return null;
        return (
          <section key={section.title} className="mt-5">
            <h4 className="text-start text-[16px] font-semibold" style={{ color: SOFT.ink }}>
              {section.title}
            </h4>
            {section.sub ? (
              <p className="mt-[3px] text-start text-[13px] leading-[18px]" style={{ color: SOFT.body }}>
                {section.sub}
              </p>
            ) : null}
            <div className="mt-3">
              <MatchGrid matches={rows} lang={lang} inTrip={inTrip} onAdd={onAdd} numbers={numbers} />
            </div>
          </section>
        );
      })}
    </div>
  );
}

/**
 * Well-known public places and local habits the model may add from general
 * knowledge (owner decision 2026-08-11). Strictly non-commercial and rendered
 * apart from catalogue rows, never with a Reserve action — the note under the
 * title is what keeps that clear.
 */
export function LocalKnowledge({
  items,
}: {
  items: NonNullable<EditorialBlock["localKnowledge"]>;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <div className="mt-5">
      <h4 className="text-start text-[15px] font-semibold" style={{ color: SOFT.ink }}>
        {t("ai_concierge.local_knowledge", { defaultValue: "Good to know locally" })}
      </h4>
      <p className="mt-1 text-[12px]" style={{ color: SOFT.body }}>
        {t("ai_concierge.local_knowledge_note", {
          defaultValue: "Public places and local habits, not part of our bookable catalogue.",
        })}
      </p>
      <div className="mt-2.5 flex flex-col gap-2">
        {items.map((it) => (
          <div
            key={it.title}
            className="px-3.5 py-2.5"
            style={{ background: SOFT.tint, borderRadius: RADIUS.tile }}
          >
            <div className="text-start text-[13.5px] font-semibold" style={{ color: SOFT.ink }}>
              {it.title}
            </div>
            <p className="mt-0.5 text-start text-[13px] leading-[18px]" style={{ color: SOFT.body }}>
              {it.note}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The three moves a good concierge makes on every answer, as fields rather
 * than as sentences the model is asked to write: the commit, the honest
 * downside, and one concrete next step. A competitor scored 78 to our 55
 * because of exactly these three habits (see the schema's note), and asking
 * for them in prose did not hold — so they render as their own lines, where
 * an empty one is visibly empty.
 */
export function EditorialAdvice({ block }: { block: EditorialBlock }) {
  const { t } = useTranslation();
  const rows = [
    block.pick ? { key: "pick", label: t("ai_concierge.advice_pick", { defaultValue: "Our pick" }), text: block.pick } : null,
    block.caution
      ? { key: "caution", label: t("ai_concierge.advice_caution", { defaultValue: "Worth knowing" }), text: block.caution }
      : null,
    block.firstMove
      ? { key: "first", label: t("ai_concierge.advice_first_move", { defaultValue: "Start here" }), text: block.firstMove }
      : null,
  ].filter((r): r is { key: string; label: string; text: string } => r !== null);
  if (rows.length === 0) return null;

  return (
    <div className="mt-4 flex flex-col gap-2.5">
      {rows.map((r) => (
        <div key={r.key}>
          <LayoutLabel>{r.label}</LayoutLabel>
          <p className="mt-1 text-start text-[14px] leading-[22px]" style={{ color: SOFT.ink }}>
            {r.text}
          </p>
        </div>
      ))}
    </div>
  );
}
